import { defineStore } from 'pinia'
import { createListLoader } from '@/libs/api/createListLoader'
import { beneficiary } from '@/libs/api'
import { isEqualNumber } from '@/libs/functions.js'

const loadBeneficiaries = createListLoader(beneficiary.getList, {
  'per-page': 50,
})

export const useBeneficiaries = defineStore('beneficiaries', {
  state: () => ({
    beneficiaries: [],
    loadProgress: null,
  }),
  actions: {
    /**
     * Load all beneficiaries
     * @returns {Promise<void>}
     */
    getList() {
      return loadBeneficiaries(this, (items) => {
        this.beneficiaries = items
      })
    },

    /**
     * Toggle pin state for a beneficiary.
     *
     * @param {string} uuid - Beneficiary uuid
     * @returns {Promise<{pinned: boolean}>}
     */
    async togglePin(uuid) {
      const item = this.beneficiaries.find((b) => b.uuid === uuid)
      if (!item) return { pinned: false }

      const wasPinned = (item.sortOrder ?? 0) > 0
      const prevSortOrder = item.sortOrder ?? null

      const nextSortOrder = wasPinned
        ? null
        : this.beneficiaries.reduce(
            (max, b) => Math.max(max, b.sortOrder ?? 0),
            0,
          ) + 1

      item.sortOrder = nextSortOrder

      try {
        await beneficiary.updateSortOrder([
          { uuid: item.uuid, sortOrder: nextSortOrder },
        ])
        return { pinned: !wasPinned }
      } catch (e) {
        item.sortOrder = prevSortOrder
        throw e
      }
    },

    /**
     * Apply beneficiary update.
     *
     * @param {{id: number, uuid: string, name: string, sortOrder: number|null,
     *   version: number, updatedAtMs: number}} payload
     */
    applyUpdate(payload) {
      const item = this.beneficiaries.find(
        (b) => isEqualNumber(b.id, payload.id) || b.uuid === payload.uuid,
      )
      if (!item) return

      const fieldsToUpdate = ['name', 'sortOrder', 'version', 'updatedAtMs']

      for (const key of fieldsToUpdate) {
        if (payload[key] !== undefined) {
          item[key] = payload[key]
        }
      }
    },
  },

  getters: {
    getBeneficiaryById(state) {
      return (id) => state.beneficiaries.find((i) => isEqualNumber(i.id, id))
    },
  },
})
